import React from 'react';
import { IUnit } from '@/types';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import './UnitInformation.scss';

interface UnitInformationProps {
  unit: IUnit | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const baseClass = 'UnitInformation';

const UnitInformation: React.FC<UnitInformationProps> = ({
  unit,
  collapsed,
  onToggleCollapse,
}) => {
  const roomsColumns: SimpleTableColumn[] = [
    {
      key: 'type',
      label: 'Room',
      width: '178px',
      sortable: true,
      showDivider: true,
      render: (value: string | null) => {
        if (!value) return <span style={{ color: '#919EAB' }}>-</span>;
        const formatted =
          value.charAt(0).toUpperCase() +
          value.slice(1).replace(/([A-Z])/g, ' $1');
        return <span>{formatted}</span>;
      },
    },
    {
      key: 'floor',
      label: 'Floor',
      width: '178px',
      sortable: true,
      showDivider: true,
      render: (value: number | null) => {
        if (value === null || value === undefined)
          return <span style={{ color: '#919EAB' }}>-</span>;
        const suffix =
          value === 1 ? 'st' : value === 2 ? 'nd' : value === 3 ? 'rd' : 'th';
        return (
          <span>
            {value}
            {suffix} floor
          </span>
        );
      },
    },
    {
      key: 'dimension',
      label: 'Dimension',
      width: '178px',
      sortable: true,
      showDivider: true,
      render: (value: number | null) => {
        if (value === null || value === undefined)
          return <span style={{ color: '#919EAB' }}>-</span>;
        return <span>{value} sqm</span>;
      },
    },
    {
      key: 'flooring',
      label: 'Flooring',
      width: '185px',
      sortable: true,
      render: (value: string | null) => {
        if (!value) return <span style={{ color: '#919EAB' }}>-</span>;
        return <span>{value}</span>;
      },
    },
  ];

  return (
    <UICollapsibleSection
      title="Unit information"
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      className="mt-24 mb-24"
    >
      <div className={`${baseClass}--content`}>
        <div className={`${baseClass}--top`}>
          <div className={`${baseClass}--top-column`}>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Floor of the building
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.floor}
              </div>
            </div>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Dimension
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.dimension}
              </div>
            </div>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Number of floors
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.numberOfFloors}
              </div>
            </div>
          </div>
          <div className={`${baseClass}--top-column`}>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Rooms
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.roomsCount}
              </div>
            </div>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Bedrooms
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.bedrooms}
              </div>
            </div>
            <div className={`${baseClass}--top-row`}>
              <div className={`${baseClass}--top-row-label`}>
                Bathrooms
              </div>
              <div className={`${baseClass}--top-row-value`}>
                {unit?.bathrooms}
              </div>
            </div>
          </div>
        </div>

        {unit?.rooms && unit.rooms.length > 0 && (
          <UISimpleTable
            data={unit.rooms}
            columns={roomsColumns}
            className={`${baseClass}--rooms-table`}
            getRowKey={(room, index) => room.id || `room-${index}`}
          />
        )}

        {unit?.otherInformation && (
          <div className={`${baseClass}--other-information`}>
            <div className={`${baseClass}--other-information-label`}>
              Other
            </div>
            <div className={`${baseClass}--other-information-value`}>
              {unit.otherInformation}
            </div>
          </div>
        )}
      </div>
    </UICollapsibleSection>
  );
};

export default UnitInformation;
